import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, User, Heart, Home, LogOut, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Header = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const hostToken = localStorage.getItem('hostToken');
  const isHostLoggedIn = !!hostToken;
  const isAnyUserLoggedIn = isAuthenticated || isHostLoggedIn;

  const handleLogout = async () => {
    if (isAuthenticated) {
      await logout();
    }
    // Clear host session
    localStorage.removeItem('hostToken');
    localStorage.removeItem('hostId');
    localStorage.removeItem('hostEmail');
    localStorage.removeItem('hostData');
    toast.success('Logged out successfully');
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_stayswap-3/artifacts/greklbdc_GEOSTAYS-01.jpg" 
              alt="GeoStays" 
              className="h-14 sm:h-16 w-auto"
            />
            <span className="hidden md:block text-xs lg:text-sm text-gray-500 font-medium border-l border-gray-300 pl-3 leading-tight max-w-[200px]">
              Finest Collection of Sustainable Homestays, Farmstays, B&Bs in India
            </span>
          </Link>

          {/* Right side - User menu */}
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/blog')}
              className="hidden md:inline-flex text-sm font-medium hover:bg-gray-100 rounded-full px-4"
              data-testid="header-blog-link"
            >
              Blog
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/host/register')}
              className="hidden md:inline-flex text-sm font-medium hover:bg-gray-100 rounded-full px-4"
            >
              Host your Home
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="flex items-center space-x-2 rounded-full px-3 py-2 border-gray-300 hover:shadow-md transition-shadow"
                >
                  <Menu className="h-4 w-4" />
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback>
                      <User className="h-4 w-4" />
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/blog')} data-testid="menu-blog-link">
                  <span>Blog</span>
                </DropdownMenuItem>
                {isAnyUserLoggedIn && (
                  <DropdownMenuItem onClick={() => navigate('/blog/mine')} data-testid="menu-my-blogs">
                    <span>My Blogs</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {!isAuthenticated ? (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/login')}>
                      <span className="font-semibold">Sign up</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/login')}>
                      Log in
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                ) : (
                  <>
                    <DropdownMenuItem disabled>
                      <div className="flex flex-col">
                        <span className="font-semibold">{user.name}</span>
                        <span className="text-xs text-gray-500">{user.email}</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/bookings')}>
                      <Home className="mr-2 h-4 w-4" />
                      <span>My bookings</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/wishlist')}>
                      <Heart className="mr-2 h-4 w-4" />
                      <span>Wishlists</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={() => {
                  const hostId = localStorage.getItem('hostId');
                  if (hostId) {
                    navigate('/host/dashboard');
                  } else {
                    navigate('/host/login');
                  }
                }}>
                  <User className="mr-2 h-4 w-4" />
                  <span>Host Login</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/host/register')}>
                  Host your Home
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/admin/login')}>
                  <ShieldCheck className="mr-2 h-4 w-4" />
                  <span>Admin Login</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/help')}>
                  Help Center
                </DropdownMenuItem>
                {isAnyUserLoggedIn && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Logout</span>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;